import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import RfiContext, { RFI_LIST_STATUS_FILTER_ALL } from './Rfi.context';
import DataRoomContext from '../DataRoom/DataRoom.context';
import dataRoomApi from '../../dataRoomApi';
import {
  DEFAULT_LIMIT,
  FIRST_PAGE,
  MAX_LIMIT,
  PAGE_PARAM_KEYWORD,
} from 'data/global';
import useQueryParams from 'hooks/useQueryParams';
import useDebounceSearch from 'hooks/useDebounceSearch';
import { sortTemplates } from '../../utils';
import {
  DataRoomRFI,
  DataRoomRFIStatusCounts,
  DataRoomRfiTemplate,
} from '../../models/DataRoomRfi';
import useRefetch from 'hooks/useRefetch';
import useTableFetchState from 'hooks/useTableFetchState';
import useUndoActions from 'hooks/useUndoActions';
import { useTranslation } from 'react-i18next';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import { DataRoomRfisUpdateEventData } from 'services/socketService/socketEventData';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import SaveAsTemplateModal from '../../subrouters/RFI/pages/RfiList/components/SaveAsTemplateModal';
import { SORTING_VALUES } from '../../subrouters/RFI/pages/RfiList/components/RfiRequestsTable/data';

type RfiProviderProps = {
  dataRoomId: string;
};

const RfiProvider: React.FC<RfiProviderProps> = (props) => {
  const { children, dataRoomId } = props;

  const {
    isLoading: isRfiListLoading,
    totalPages: rfiListTotalPages,
    totalItems: rfiListTotalItems,
    data: rfiListItems,
    setIsLoading: setIsRfiListLoading,
    setData: setRfiListItems,
    setPaginatedData: setRfiListPaginatedData,
  } = useTableFetchState<DataRoomRFI>();

  const [rfiListStatusFilter, setRfiListStatusFilter] = useState(
    RFI_LIST_STATUS_FILTER_ALL,
  );

  const [rfiListStatusCounts, setRfiListStatusCounts] =
    useState<DataRoomRFIStatusCounts>(null);
  const [rfiListStatusCountsFilter, setRfiListStatusCountsFilter] = useState(
    RFI_LIST_STATUS_FILTER_ALL,
  );

  const { params, setQueryParam } = useQueryParams();

  const {
    sort = `${SORTING_VALUES.displayStatus} -${SORTING_VALUES.dueDate}`,
    page,
  } = params;

  const onRfiListSearch = useCallback(
    () => setQueryParam(PAGE_PARAM_KEYWORD, FIRST_PAGE),
    [setQueryParam],
  );

  const {
    searchString: rfiListSearchString,
    searchInputValue: rfiListSearchInputValue,
    onSearchInputChange: onRfiListSearchInputChange,
  } = useDebounceSearch({ onSearch: onRfiListSearch });

  const rfiListFilterParams = useMemo(
    () => ({
      ...(rfiListSearchString && { $searchString: rfiListSearchString }),
      ...(rfiListStatusFilter !== RFI_LIST_STATUS_FILTER_ALL && {
        displayStatus: rfiListStatusFilter,
      }),
    }),
    [rfiListSearchString, rfiListStatusFilter],
  );

  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [rfiTemplates, setRfiTemplates] = useState<DataRoomRfiTemplate[]>([]);

  const [rfiToRemove, setRfiToRemove] = useState<DataRoomRfiTemplate>();

  const [selectedRfi, setSelectedRfi] = useState<DataRoomRFI>();

  const [saveAsTemplateModal, saveAsTemplateModalRef] =
    useCallbackRef<ModalRef>();

  const { isAdmin, fetchRfiUnseenCount } = useContext(DataRoomContext);

  const { refetchFlag, refetch } = useRefetch();
  const { refetchFlag: refetchFlagTemplates, refetch: refetchTemplates } =
    useRefetch();
  const {
    refetchFlag: refetchFlagRfiStatusCounts,
    refetch: refetchRfiStatusCounts,
  } = useRefetch();

  const { t } = useTranslation();

  const getRfiTemplateById = useCallback(
    (templateId: string) => rfiTemplates.find(({ _id }) => _id === templateId),
    [rfiTemplates],
  );

  const isEmpty =
    !rfiListTotalItems &&
    rfiListStatusFilter === RFI_LIST_STATUS_FILTER_ALL &&
    !rfiListSearchString;

  const updateRfiListItem = useCallback(
    (updatedRfi: Partial<DataRoomRFI>) => {
      setRfiListItems((prev) =>
        prev.map((item) =>
          item._id === updatedRfi._id ? { ...item, ...updatedRfi } : item,
        ),
      );
    },
    [setRfiListItems],
  );

  const onRfiListStatusFilterChange = useCallback(
    (value: string) => {
      setRfiListStatusFilter(value);
      setQueryParam(PAGE_PARAM_KEYWORD, FIRST_PAGE);
    },
    [setQueryParam],
  );

  const { isUndoAvailable, setUndoTimeout, clearUndoTimeout } =
    useUndoActions();

  const {
    isUndoAvailable: isActionUndoAvailable,
    setUndoTimeout: setActionUndoTimeout,
    clearUndoTimeout: clearActionUndoTimeout,
  } = useUndoActions();

  const [actionUndoContent, setActionUndoContent] = useState<ReactNode>();

  const onActionUndoRef = useRef<() => void>();

  const showActionUndo = useCallback(
    (content: ReactNode, onUndo: () => void, onExpire?: () => void) => {
      onActionUndoRef.current = onUndo;
      setActionUndoContent(content);
      setActionUndoTimeout(() => {
        setActionUndoContent(null);
        onExpire?.();
      });
    },
    [setActionUndoTimeout],
  );

  const onActionUndo = useCallback(() => {
    clearActionUndoTimeout();
    onActionUndoRef.current?.();
    setActionUndoContent(null);
  }, [clearActionUndoTimeout]);

  const onDeleteRequest = useCallback(
    (templateId: string) => {
      dataRoomApi.rfiTemplates.remove(dataRoomId, templateId);
    },
    [dataRoomId],
  );

  const onUndoDeleteRequest = useCallback(
    (templateId: string) => {
      dataRoomApi.rfiTemplates.undoRemove(dataRoomId, templateId);
    },
    [dataRoomId],
  );

  const onUndo = useCallback(async () => {
    try {
      clearUndoTimeout();
      onUndoDeleteRequest(rfiToRemove?._id);
      setRfiTemplates((prev) => sortTemplates([rfiToRemove, ...prev]));
      setRfiToRemove(null);
    } catch {
      return;
    }
  }, [clearUndoTimeout, onUndoDeleteRequest, rfiToRemove]);

  const deleteTemplate = useCallback(
    (templateId: string) => {
      clearUndoTimeout();

      setRfiTemplates((prev) =>
        prev.filter((t) => {
          if (t._id !== templateId) {
            return true;
          }

          setRfiToRemove(t);
          return false;
        }),
      );

      onDeleteRequest(templateId);
      setUndoTimeout(() => setRfiToRemove(null));
    },
    [clearUndoTimeout, onDeleteRequest, setUndoTimeout],
  );

  const openSaveAsTemplateModal = useCallback(
    (rfi: DataRoomRFI) => {
      setSelectedRfi(rfi);
      saveAsTemplateModal?.open();
    },
    [saveAsTemplateModal],
  );

  useEffect(() => {
    if (!isAdmin) return;

    (async () => {
      setIsRfiListLoading(true);

      try {
        const { data } = await dataRoomApi.rfi.getRfis(dataRoomId, {
          $sort: sort,
          $page: page,
          $limit: DEFAULT_LIMIT,
          $relations: '["dataRoomRFICategories"]',
          ...rfiListFilterParams,
        });

        setRfiListPaginatedData(data);
      } catch (e) {
        return;
      } finally {
        setIsRfiListLoading(false);
      }
    })();
  }, [
    dataRoomId,
    isAdmin,
    sort,
    page,
    rfiListFilterParams,
    refetchFlag,
    setIsRfiListLoading,
    setRfiListPaginatedData,
  ]);

  useEffect(() => {
    if (!isAdmin) return;

    (async () => {
      try {
        const { data } = await dataRoomApi.rfi.getRfiStatusCounts(
          dataRoomId,
          rfiListFilterParams,
        );

        setRfiListStatusCounts(data);
        setRfiListStatusCountsFilter(rfiListStatusFilter);
      } catch (e) {
        return;
      }
    })();
  }, [
    dataRoomId,
    isAdmin,
    rfiListFilterParams,
    rfiListStatusFilter,
    refetchFlag,
    refetchFlagRfiStatusCounts,
  ]);

  useEffect(() => {
    if (!isAdmin) return;

    (async () => {
      setIsLoadingTemplates(true);
      try {
        const {
          data: { items },
        } = await dataRoomApi.rfiTemplates.get(dataRoomId, {
          $limit: MAX_LIMIT,
        });

        setRfiTemplates(sortTemplates(items));
      } catch (e) {
        return;
      } finally {
        setIsLoadingTemplates(false);
      }
    })();
  }, [dataRoomId, isAdmin, refetchFlagTemplates]);

  const onRfisUpdated = useCallback(
    ({ dataRoomId: dataRoomIdPayload }: DataRoomRfisUpdateEventData) => {
      if (dataRoomId === dataRoomIdPayload) {
        refetch();
        fetchRfiUnseenCount();
      }
    },
    [dataRoomId, refetch, fetchRfiUnseenCount],
  );

  useSocketEvent(LISTEN_EVENTS.RFIS_UPDATED, onRfisUpdated);

  return (
    <RfiContext.Provider
      value={{
        isLoadingTemplates,
        rfiTemplates,
        isRfiListLoading,
        rfiListItems,
        rfiListTotalPages,
        rfiListTotalItems,
        rfiListStatusFilter,
        rfiListSearchInputValue,
        rfiListStatusCounts,
        rfiListStatusCountsFilter,
        isEmpty,
        onRfiListStatusFilterChange,
        onRfiListSearchInputChange,
        updateRfiListItem,
        refetch,
        refetchRfiStatusCounts,
        refetchTemplates,
        deleteTemplate,
        getRfiTemplateById,
        openSaveAsTemplateModal,
        showActionUndo,
      }}
    >
      {children}

      <PopNotificationWithUndo
        isUndoAvailable={isUndoAvailable}
        onUndo={onUndo}
      >
        <p>{t('templateDeleted')}</p>
      </PopNotificationWithUndo>

      <PopNotificationWithUndo
        isUndoAvailable={isActionUndoAvailable}
        onUndo={onActionUndo}
      >
        <p>{actionUndoContent}</p>
      </PopNotificationWithUndo>

      <SaveAsTemplateModal ref={saveAsTemplateModalRef} rfi={selectedRfi} />
    </RfiContext.Provider>
  );
};

export default RfiProvider;
